import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';

// Importando as variáveis de controle do seu arquivo de configuração oficial
import { camara, KEY_REF } from './adm/admin/firebaseConfig';

// Ícones estruturais para UI/UX
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import EscalatorWarningRoundedIcon from '@mui/icons-material/EscalatorWarningRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import FolderZipRoundedIcon from '@mui/icons-material/FolderZipRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';

// Identificadores de Gurjão baseados no seu App.js/firebaseConfig.js
// Prefeitura de Gurjão (2506509) ou Câmara de Gurjão (25065091)
const isGurjao = KEY_REF === '2506509' || KEY_REF === '25065091';

export default function MapaDoSite() {
    
    // 1. Montagem dinâmica da seção Institucional conforme o tipo de órgão (Câmara vs Prefeitura)
    const obterLinksInstitucionais = () => {
        const links = [
            { path: '/', label: 'Página Inicial (Início)' },
            { path: '/historia', label: 'História do Município' },
            { path: '/noticias/todas', label: 'Todas as Notícias' },
            { path: '/avisos', label: 'Avisos e Alertas' },
            { path: '/galeria', label: 'Galeria de Mídias' },
        ];

        // Se for Câmara, exibe links específicos do legislativo
        if (camara) {
            links.push({ path: '/vereadores', label: 'Vereadores / Corpo Legislativo' });
            links.push({ path: '/mesadiretora', label: 'Mesa Diretora' });
            links.push({ path: '/galeria-presidentes', label: 'Galeria de Presidentes' });
        } else {
            // Se for prefeitura, exibe secretários e gestão institucional
            links.push({ path: '/secretarios', label: 'Secretarias e Secretários' });
        }
        return links;
    };

    // 2. Montagem dinâmica da Transparência
    const obterLinksTransparencia = () => {
        const links = [
            { path: '/transparencia', label: 'Portal da Transparência' },
            { path: '/e-sic', label: 'E-SIC (Acesso à Informação)' },
            { path: '/ouvidoria', label: 'Ouvidoria Geral' },
        ];

        if (!camara) {
            links.push({ path: '/licitacoes', label: 'Painel Geral de Licitações' });
            links.push({ path: '/saladoempreendedor', label: 'Sala do Empreendedor' });
            links.push({ path: '/leipaulogustavo', label: 'Lei Paulo Gustavo' });
            links.push({ path: '/lgpd', label: 'LGPD (Proteção de Dados)' });
            links.push({ path: '/carta-de-servico', label: 'Carta de Serviços' });
        }
        return links;
    };

    // 3. Montagem dinâmica de Legislação (Controle de rotas exclusivas de Câmara / Prefeitura)
    const obterLinksLegislacao = () => {
        const links = [
            { path: '/legislacao/leismunicipais', label: 'Legislação Municipal' },
            { path: '/legislacao/leiorganica', label: 'Lei Orgânica' },
            { path: '/legislacao/codigotributario', label: 'Código Tributário' },
            { path: '/legislacao/decretos', label: 'Decretos Municipais' },
            { path: '/legislacao/portarias', label: 'Portarias' },
            { path: '/legislacao/diariosoficial', label: 'Diário Oficial' },
            { path: '/legislacao/requerimentos', label: 'Requerimentos' },
            { path: '/legislacao/projetodelei', label: 'Projetos de Lei' },
            { path: '/legislacao/decretos_legislativos', label: 'Decretos Legislativos' },
            { path: '/legislacao/mocoes', label: 'Moções' },
            { path: '/legislacao/atas', label: 'Atas das Sessões' },
            { path: '/legislacao/emendas_parlamentares', label: 'Emendas Parlamentares' },
            { path: '/legislacao/resolucoes', label: 'Resoluções' },
            { path: '/legislacao/indicacoes', label: 'Indicações' },
            { path: '/legislacao/pareceres', label: 'Pareceres Técnicos' },
            { path: '/legislacao/pedidos_info', label: 'Pedidos de Informação' },
            { path: '/legislacao/controleinterno', label: 'Controle Interno' },
        ];

        if (!camara) {
            links.push({ path: '/processo_seletivo', label: 'Processos Seletivos' });
            links.push({ path: '/legislacao/aldir_blanc" ', label: 'Lei Aldir Blanc' });
        }
        return links;
    };

    // 4. Montagem dinâmica da seção de Arquivos e Publicações Oficiais
    const obterLinksArquivos = () => {
        const links = [
            { path: '/arquivos/editais', label: 'Editais' },
            { path: '/arquivos/contratos', label: 'Contratos' },
            { path: '/arquivos/licitacoes', label: 'Arquivos de Licitações' },
            { path: '/arquivos/ldo', label: 'LDO (Lei de Diretrizes Orçamentárias)' },
            { path: '/arquivos/loa', label: 'LOA (Lei Orçamentária Anual)' },
            { path: '/arquivos/ppa', label: 'PPA (Plano Plurianual)' },
            { path: '/arquivos/pca', label: 'PCA (Prestação de Contas Anual)' },
            { path: '/arquivos/concursos', label: 'Concursos Públicos' },
            { path: '/arquivos/semanario', label: 'Semanário Oficial' },
            { path: '/arquivos/quadro', label: 'Quadro de Pessoal' },
            { path: '/arquivos/tabela-diarias', label: 'Tabela de Diárias Municipais' },
            { path: '/arquivos/frota', label: 'Frota Municipal' },
        ];

        // Publicações restritas por lei a prefeituras (RGF e RREO não rodam em Câmara se camara=true no App)
        if (!camara) {
            links.push({ path: '/arquivos/rgf', label: 'RGF (Relatório de Gestão Fiscal)' });
            links.push({ path: '/arquivos/rreo', label: 'RREO (Relatório Resumido da Execução Orçamentária)' });
            links.push({ path: '/arquivos/farmaciabasica', label: 'Remédios / Farmácia Básica' });
        }
        return links;
    };

    // 5. Montagem dinâmica de Serviços e Aplicativos (Validações de Câmara e de Gurjão)
    const obterLinksServicos = () => {
        const links = [
            { path: '/app/perguntas', label: 'Contato / Perguntas Frequentes' }
        ];

        if (camara) {
            links.push({ path: '/app/sessoes', label: 'Sessões da Câmara' });
        }

        // Itens que só aparecem em PREFEITURA
        if (!camara) {
            links.push({ path: '/app/notasfiscais', label: 'Nota Fiscal Eletrônica' });
            links.push({ path: '/app/iptu', label: 'IPTU Online' });
            links.push({ path: '/app/acessorapido', label: 'Acesso Rápido' });
            
            // Filtro específico para exibir os itens exclusivos de GURJÃO
            if (isGurjao) {
                links.push({ path: '/app/gurjaoplus', label: 'Programa Gurjão+' });
                links.push({ path: '/app/gurjaoplus/politicadeprivacidade', label: 'Gurjão+ (Política de Privacidade)' });
                links.push({ path: '/turismo/bodenarua', label: 'Turismo - Festival Bode na Rua' });
            }
        }
        return links;
    };

    // 6. Área de Recursos Humanos (Exclusiva de prefeituras no App.js)
    const obterLinksRH = () => {
        if (camara) return []; // Retorna vazio se for câmara municipal
        
        return [
            { path: '/rh/documentos', label: 'Solicitações e Documentos de RH' },
            { path: '/rh/fichafuncional', label: 'Ficha Funcional do Servidor' },
            { path: '/rh/cc', label: 'Contracheque Online' }
        ];
    };

    // Estrutura unificada de cards filtrados para renderizar na UI
    const sitemapEstrutura = [
        { titulo: camara ? 'Institucional Legislativo' : 'Institucional Executivo', icone: <AccountBalanceRoundedIcon color="primary" />, links: obterLinksInstitucionais() },
        { titulo: 'Transparência Pública', icone: <EscalatorWarningRoundedIcon color="primary" />, links: obterLinksTransparencia() },
        { titulo: 'Atos e Legislações', icone: <GavelRoundedIcon color="primary" />, links: obterLinksLegislacao() },
        { titulo: 'Publicações e Arquivos Oficiais', icone: <FolderZipRoundedIcon color="primary" />, links: obterLinksArquivos() },
        { titulo: 'Serviços e Canais Digitais', icone: <DevicesRoundedIcon color="primary" />, links: obterLinksServicos() },
        // Só renderiza o card de RH se houver links (ou seja, se não for câmara)
        ...(camara ? [] : [{ titulo: 'Recursos Humanos (Servidor)', icone: <BadgeRoundedIcon color="primary" />, links: obterLinksRH() }])
    ];

    return (
        <Box sx={{ bgcolor: '#f8fafc', py: 6, minHeight: '80vh' }}>
            <Container maxWidth="lg">
                
                {/* Cabeçalho do Mapa */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography 
                        variant="h4" 
                        component="h1" 
                        fontWeight={800} 
                        color="text.primary"
                        gutterBottom
                        sx={{ letterSpacing: '-0.5px', textTransform: 'uppercase' }}
                    >
                        Mapa do Site
                    </Typography>
                    <Typography variant="body1" color="text.secondary" maxWidth="600px" sx={{ mx: 'auto' }}>
                        Localize de forma ágil todas as páginas de conteúdo, documentos legais, índices e canais de atendimento ativos nesta edilidade.
                    </Typography>
                </Box>

                {/* Grid Responsivo de Cards */}
                <Grid container spacing={3}>
                    {sitemapEstrutura.map((secao, idx) => (
                        <Grid item xs={12} md={6} key={idx}>
                            <Card 
                                variant="outlined"
                                sx={{ 
                                    height: '100%', 
                                    borderRadius: '12px',
                                    borderColor: '#e2e8f0',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        {secao.icone}
                                        <Typography variant="h6" component="h2" fontWeight={700} color="text.primary">
                                            {secao.titulo}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 1.5 }} />
                                    
                                    <List disablePadding>
                                        {secao.links.map((link, linkIdx) => (
                                            <ListItem disablePadding key={linkIdx}>
                                                <ListItemButton 
                                                    component={RouterLink} 
                                                    to={link.path}
                                                    sx={{ 
                                                        px: 1, 
                                                        py: 0.75, 
                                                        borderRadius: '6px',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                                                            color: 'primary.main'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: '28px', color: 'text.disabled' }}>
                                                        <LinkRoundedIcon sx={{ fontSize: '16px' }} />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={link.label} 
                                                        primaryTypographyProps={{ 
                                                            variant: 'body2', 
                                                            fontWeight: 500
                                                        }} 
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}